import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { currentPathSuffix, validateReturnTo } from "../auth/returnTo";
import type { AuthState } from "./reducers/authStateReducer";

export const isUnauthorized = (error: FetchBaseQueryError): boolean =>
  error.status === 401 || (error.status === "PARSING_ERROR" && error.originalStatus === 401);

const isSignedIn = (state: unknown): boolean => {
  const authState = (state as { authState?: { state?: AuthState | null } }).authState?.state;
  return authState?.type === "LoggedIn" || authState?.type === "LoggedInWithOrgContext";
};

export const loginUrlReturningTo = (location: { pathname: string; search: string; hash: string }): string => {
  const returnTo = validateReturnTo(currentPathSuffix(location));
  return returnTo ? `/login?return_to=${encodeURIComponent(returnTo)}` : "/login";
};

type Listener = () => void;
const signedOutDuringSaveListeners = new Set<Listener>();

export const onSignedOutDuringSave = (listener: Listener): (() => void) => {
  signedOutDuringSaveListeners.add(listener);
  return () => {
    signedOutDuringSaveListeners.delete(listener);
  };
};

const announceSignedOutDuringSave = () => {
  for (const listener of signedOutDuringSaveListeners) listener();
};

export const redirectToLoginOnExpiredSession = (
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  goToLogin: (url: string) => void = (url) => window.location.assign(url),
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  let redirecting = false;
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    if (!redirecting && result.error && isUnauthorized(result.error) && isSignedIn(api.getState())) {
      if (api.type === "mutation") {
        announceSignedOutDuringSave();
      } else {
        redirecting = true;
        goToLogin(loginUrlReturningTo(window.location));
      }
    }
    return result;
  };
};
